import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { usePostHog } from "posthog-js/react";
import { usePubNub } from "pubnub-react";

import { getShipmentIdFromShipmentBookedChatName } from "utils/chat";

import { useAPI } from "./useAPI";
import { useAuthContext } from "./useAuthContext";
import { ClientAppEvent } from "../constants";

import type { StatusEvent } from "pubnub";

interface UseChatPublishTokenProps {
  currentChannel: string;
}

interface PublishTokenResponse {
  token: string;
}

// "cold_open": proactive fetch on opening a chat outside the hot set.
// "access_denied_retry": reactive self-heal after the active token was rejected.
type PublishTokenFetchSource = "cold_open" | "access_denied_retry";

const PUBNUB_ACCESS_DENIED_CATEGORY = "PNAccessDeniedCategory";

/**
 * Reconciles the single active PubNub token to whichever chat is open.
 * PubNub's `setToken` replaces (never merges), so we hold one token at a time:
 * the base token for hot-set channels (write already granted) and an on-demand
 * single-channel publish token for cold channels. Every token carries group
 * read, so subscribe/receive is never affected by a swap. Returns `canSend`
 * (false until the correct token is set) and an `onStatus` handler that
 * self-heals a rejected token.
 */
export function useChatPublishToken(props: UseChatPublishTokenProps) {
  const { currentChannel } = props;
  const { pubnub: pubnubUserInfo } = useAuthContext();
  const pubnubClient = usePubNub();
  const { api } = useAPI();
  const posthog = usePostHog();

  const baseToken = pubnubUserInfo?.token ?? null;
  const hotSet = useMemo(
    () => pubnubUserInfo?.writable_channel_names ?? [],
    [pubnubUserInfo],
  );

  // Per-channel publish tokens fetched this session. A ref (not state) so the
  // cache neither triggers re-renders nor resets across channel switches.
  const publishTokenCacheRef = useRef<Map<string, string>>(new Map());
  // The channel the open chat currently expects a token for, tracked in a ref
  // so an in-flight fetch can detect it was superseded by a faster channel
  // switch and avoid clobbering the newer channel's token.
  const activeChannelRef = useRef<string | null>(null);
  // The cold channel whose fetched token is now active. `canSend` is derived
  // from it, so a channel switch gates the input until the swap resolves
  // without a synchronous setState in the effect.
  const [readyChannel, setReadyChannel] = useState<string | null>(null);

  const fetchPublishToken = useCallback(
    async (
      channelName: string,
      source: PublishTokenFetchSource,
    ): Promise<string | null> => {
      const shipmentId = getShipmentIdFromShipmentBookedChatName(channelName);
      if (shipmentId === "") return null;
      const response = await api<PublishTokenResponse>(
        "get",
        `/chat/shipments/${shipmentId}/publish-token`,
      );
      const { token } = response.data;
      publishTokenCacheRef.current.set(channelName, token);
      posthog?.capture(ClientAppEvent.INSTANT_CHAT_PUBLISH_TOKEN_FETCHED, {
        source,
        channel: channelName,
      });
      return token;
    },
    [api, posthog],
  );

  // Resolves true once the channel's token is the active one. Returns false
  // without swapping when there is no base token, the channel name carries no
  // shipment id, or a later channel switch superseded this fetch.
  const applyTokenForChannel = useCallback(
    async (
      channelName: string,
      source: PublishTokenFetchSource,
    ): Promise<boolean> => {
      if (baseToken === null) return false;
      if (hotSet.includes(channelName)) {
        pubnubClient.setToken(baseToken);
        return true;
      }
      const cached = publishTokenCacheRef.current.get(channelName);
      const token = cached ?? (await fetchPublishToken(channelName, source));
      if (token === null || activeChannelRef.current !== channelName) {
        return false;
      }
      pubnubClient.setToken(token);
      return true;
    },
    [baseToken, hotSet, pubnubClient, fetchPublishToken],
  );

  useEffect(() => {
    activeChannelRef.current = currentChannel;
    applyTokenForChannel(currentChannel, "cold_open")
      .then((applied) => {
        if (applied && activeChannelRef.current === currentChannel) {
          setReadyChannel(currentChannel);
        }
      })
      .catch(() => {
        // Token reconcile failed (e.g. the publish-token fetch errored). Leave
        // the input gated; a channel re-open or access-denied self-heal retries.
        if (activeChannelRef.current === currentChannel) {
          setReadyChannel(null);
        }
      });
  }, [currentChannel, applyTokenForChannel]);

  // Hot-set channels are sendable immediately — the base token already grants
  // them write, so no swap round-trip gates the input.
  const isHotChannel = baseToken !== null && hotSet.includes(currentChannel);
  const canSend = isHotChannel || readyChannel === currentChannel;

  const handlePubnubStatus = useCallback(
    (event: StatusEvent): void => {
      if (event.category !== PUBNUB_ACCESS_DENIED_CATEGORY) return;
      // The active token was rejected (expiry, eviction, or a swap race). Drop
      // the stale cached token and re-fetch so the next send succeeds.
      publishTokenCacheRef.current.delete(currentChannel);
      // Fire-and-forget: a failed re-fetch leaves the rejected token in place,
      // and the next access-denied event triggers another retry.
      applyTokenForChannel(currentChannel, "access_denied_retry").catch(
        () => {},
      );
    },
    [currentChannel, applyTokenForChannel],
  );

  return { canSend, handlePubnubStatus };
}
